<!-->┌──────────────────────────────────────────────────────────────────────────────────────┐<- ->
<- ->│                                   Contact Info                                       │<- ->
<- ->└──────────────────────────────────────────────────────────────────────────────────────┘<--->

<template>
    <div class="rewards-contact-info" v-if="loaded">
        <form @submit.prevent="updateMember">

            <h3 class="rewards-title">Your Contact Info</h3>

            <!-- NAME -->
            <div class="member-id">

                <div class="full-name">
                    <!-- Title -->
                    <div class="labeled-text-input static title-field">
                        <label for="title">Title</label>
                        <input type="text" id="title" v-model="title" disabled />
                    </div>

                    <!-- First & Last Name -->
                    <div class="labeled-text-input static name-field">
                        <label for="fullName">Name</label>
                        <input type="text" id="fullName" :value="fullName" readonly disabled />
                    </div>
                </div>

                <date-dmy v-model="dateOfBirth" key="DateOfBirth" class="date-of-birth-input">
                    <label for="date-of-birth">Date Of Birth</label>
                </date-dmy>
            </div>

            <!-- CONTACT -->
            <div class="contact-info">

                <!-- Email -->
                <labeled-text-input name="email" id="memberEmail" class="member-email" type="email"
                                    v-model="email" has-icon>
                    Email
                    <template #addendum>
                        <span v-if="errors.Email" class="error">{{ errors.Email }}</span>
                        <span v-if="errors.EmailTooLong" class="error">{{ errors.EmailTooLong }}</span>
                    </template>
                </labeled-text-input>

                <div class="member-phones">
                    <!-- Primary Phone -->
                    <div class="member-phone-1">

                        <!-- Country Code -->
                        <div class="labeled-select-input country-code">
                            <label>Phone Country Code</label>
                            <select v-model="phoneCountryCode">
                                <option value="null" disabled>Select a country</option>
                                <option v-for="(countryCode, index) in countryCodes" :key="index" :value="countryCode.code">
                                    {{ countryCode.title }} {{ countryCode.code ? `(+${countryCode.code})` : '' }}
                                </option>
                            </select>
                        </div>

                        <!-- Number -->
                        <labeled-text-input name="phoneNumber" id="phoneNumber" class="phone-input"
                                            v-model="phoneNumber" has-icon>
                            Phone 1
                            <template #addendum>
                                <span v-if="errors.PhoneNumber" class="error">{{ errors.PhoneNumber }}</span>
                                <span v-if="errors.PhoneNumberTooLong" class="error">{{ errors.PhoneNumberTooLong }}</span>
                            </template>
                        </labeled-text-input>

                        <!-- Type -->
                        <div class="labeled-select-input phone-type">
                            <label>Type</label>
                            <select v-model="phoneType">
                                <option value="null" disabled>Type</option>
                                <option value="H">Home</option>
                                <option value="B">Business</option>
                                <option value="M" selected>Mobile</option>
                            </select>
                        </div>
                    </div>

                    <!-- Alternate Phone -->
                    <div class="member-phone-2">

                        <!-- Country Code -->
                        <div class="labeled-select-input country-code">
                            <label>Phone Country Code</label>
                            <select v-model="phone2CountryCode">
                                <option value="null" disabled>Select a country</option>
                                <option v-for="(countryCode, index) in countryCodes" :key="index" :value="countryCode.code">
                                    {{ countryCode.title }} {{ countryCode.code ? `(+${countryCode.code})` : '' }}
                                </option>
                            </select>
                        </div>

                        <!-- Number -->
                        <labeled-text-input name="phone2Number" id="phone2Number" class="phone-input"
                                            v-model="phone2Number" has-icon>
                            Phone 2
                            <template #addendum>
                                <span v-if="errors.Phone2Number" class="error">{{ errors.Phone2Number }}</span>
                                <span v-if="errors.Phone2NumberTooLong" class="error">{{ errors.Phone2NumberTooLong }}</span>
                            </template>
                        </labeled-text-input>

                        <!-- Type -->
                        <div class="labeled-select-input phone-type">
                            <label>Type</label>
                            <select v-model="phone2Type">
                                <option value="null" disabled>Type</option>
                                <option value="H">Home</option>
                                <option value="B">Business</option>
                                <option value="M" selected>Mobile</option>
                            </select>
                        </div>
                    </div>

                </div>

            </div>

            <h5>Mailing Address</h5>

            <!-- ADDRESS -->
            <div class="mailing-address">
                <!-- Street -->
                <labeled-text-input name="addressOne" id="addressOne" class="address line-1" v-model="addressLineOne" has-icon>
                    Address Line 1
                    <template #addendum>
                        <span v-if="errors.AddressLineOne" class="error">{{ errors.AddressLineOne }}</span>
                        <span v-if="errors.AddressLineOneTooLong" class="error">{{ errors.AddressLineOneTooLong }}</span>
                    </template>
                </labeled-text-input>
                <labeled-text-input name="addressTwo" id="addressTwo" class="address line-2" v-model="addressLineTwo">
                    Address Line 2
                    <template #addendum>
                        <span v-if="errors.AddressLineTwoTooLong" class="error">{{ errors.AddressLineTwoTooLong }}</span>
                    </template>
                </labeled-text-input>

                <!-- City -->
                <labeled-text-input name="city" id="city" class="address-city" v-model="addressCity">
                    City
                    <template #addendum>
                        <span v-if="errors.AddressCity" class="error">{{ errors.AddressCity }}</span>
                        <span v-if="errors.AddressCityTooLong" class="error">{{ errors.AddressCityTooLong }}</span>
                    </template>
                </labeled-text-input>

                <!-- State/Province -->
                <labeled-text-input name="stateProvince" id="stateProvince" class="address-state" v-model="addressState">
                    State/Province
                    <template #addendum>
                        <span v-if="errors.AddressState" class="error">{{ errors.AddressState }}</span>
                        <span v-if="errors.AddressStateTooLong" class="error">{{ errors.AddressStateTooLong }}</span>
                    </template>
                </labeled-text-input>

                <!-- Postal -->
                <labeled-text-input name="postalCode" id="postalCode" class="address-postal" v-model="addressZip">
                    Postal Code
                    <template #addendum>
                        <span v-if="errors.AddressZip" class="error">{{ errors.AddressZip }}</span>
                        <span v-if="errors.AddressZipTooLong" class="error">{{ errors.AddressZipTooLong }}</span>
                    </template>
                </labeled-text-input>

                <!-- Country -->
                <div class="address-country labeled-select-input">
                    <label>Country</label>
                    <select v-model="addressCountryCode">
                        <option value="null" disabled>Select a country code</option>
                        <option v-for="(countryCode, index) in countryCodes" :key="index" :value="countryCode.country">{{ countryCode.title }}</option>
                    </select>
                </div>
            </div>

            <!-- DOCUMENT/PASSPORT -->
            <div class="member-document">
                <h5>
                    <template v-if="CONFIG.AirlineName && CONFIG.AirlineNicName">
                        Please enter {{ CONFIG.AirlineName }} {{ CONFIG.AirlineNicName }} Number or Passport
                    </template>
                    <template v-else>
                        Please enter National ID Number or Passport
                    </template>
                </h5>

                <div class="id-passport">

                    <div class="national-id">

                        <span v-if="errors.NatIdPassportTooLong" class="error">{{ errors.NatIdPassportTooLong }}</span>

                        <!--National ID-->
                        <labeled-text-input name="nationalId" id="nationalId" class="member-document" v-model="nationalIdNumber">
                            <template v-if="CONFIG.AirlineName && CONFIG.AirlineNicName">
                                {{ CONFIG.AirlineName }} {{ CONFIG.AirlineNicName }} Number
                            </template>
                            <template v-else>
                                National ID Number
                            </template>
                        </labeled-text-input>

                    </div>

                    <div class="passport">

                        <span v-if="errors.PassportNumberTooLong" class="error">{{ errors.PassportNumberTooLong }}</span>

                        <!--Passport Number-->
                        <labeled-text-input name="passport" id="passport" class="number member-document has-icon" v-model="passportNumber">
                            Passport Number
                        </labeled-text-input>

                        <date-dmy v-model="passportExpires" :future="true" class="passport-expires">
                            <label for="passport-expires">Expires</label>
                        </date-dmy>

                        <!--Issuing Country-->
                        <div class="issuing-country labeled-select-input">
                            <label>Issuing Country</label>
                            <select v-model="passportIssuingCountry">
                                <option value="null" disabled>Select a country</option>
                                <option v-for="(countryCode, index) in countryCodes" :key="index" :value="countryCode.country">{{ countryCode.title }}</option>
                            </select>
                            <span v-if="errors.PassportIssuingCountry" class="error">{{ errors.PassportIssuingCountry }}</span>
                        </div>

                        <div class="nationality labeled-select-input">
                            <label>Nationality</label>
                            <select v-model="passportNationality">
                                <option value="null" disabled>Nationality</option>
                                <option :value="country.country" v-for="country in countryCodes">{{ country.title }}</option>
                            </select>
                            <span v-if="errors.PassportNationality" class="error">{{ errors.PassportNationality }}</span>

                        </div>
                    </div>
                </div>
            </div>
            
            <!-- CONFIRM DUPLICATE EMAIL -->
            <label class="allow-duplicate-email" v-if="showDuplicateEmailCheckbox">
                <input type="checkbox" v-model="allowDuplicateEmailAddress" />
                <span>I want to re-use this email address: <strong>{{ email }}</strong></span>
            </label>

            <!-- FORM SUBMIT -->
            <div class="user-action">
                <button class="button-s" name="submit" type="submit">Submit</button>
            </div>
        </form>

        <!-- CONFLICTS -->
        <transition-expand v-if="error || exception" classList="conflicts-container">
            <conflicts :error.sync="error" :exception.sync="exception" :is-sys-admin="isSysAdmin"></conflicts>
        </transition-expand>
    </div>
</template>

<script>
    import { fetchJSON, postJSON } from "@/helpers/fetch"
    import { isoDate, toDate } from "@/helpers/dates"

    import conflicts, { CONFLICTS } from "@/common/conflicts"
    import dateDmy from "@/common/date-dmy"

    export default {
        components: { conflicts, dateDmy },
        mixins: [CONFLICTS],

        emits: ['loading', 'loaded', 'submitting', 'submitted', 'close', 'update:member-details'],

        name: 'ContactInfo',

        props: {
            memberDetails: Object,
            portalInfo: Object
        },

        data() {
            return {

                title: null,
                dateOfBirth: '',
                email: null,
                fullName: null,

                phoneCountryCode: null,
                phoneNumber: null,
                phoneType: null,

                phone2CountryCode: null,
                phone2Number: null,
                phone2Type: null,

                addressLineOne: null,
                addressLineTwo: null,
                addressCity: null,
                addressState: null,
                addressZip: null,
                addressCountryCode: null,

                nationalIdNumber: null,
                nationalIdIssuingCountry: null,

                passportNumber: null,
                passportExpires: null,
                passportIssuingCountry: null,
                passportNationality: null,

                countryCodes: [''],
                errors: {},
                loaded: false,

                submitting: false,

                showDuplicateEmailCheckbox: false,
                allowDuplicateEmailAddress: false,
            }
        },

        mounted() {
            let endpoints = [this.getPhoneCountryCodes()]
            if (!this.memberDetails) endpoints.push(this.getMemberDetails())

            if (this.memberDetails) this.processMemberData(this.memberDetails.member);

            this.$emit('loading')

            Promise.all(endpoints)
                .then(() => {
                    this.loaded = true
                    this.$emit('loaded')
                })
                .catch(() => this.$emit('close'))
        },

        watch: {
            enrollmentSuccess() {
                this.clearError()
            }
        },

        methods: {

            getMemberDetails() {
                return fetchJSON(`/api/members/GetMemberDetails?verbose=true`)
                    .then(data => {
                        this.member = this.processMemberData(data.member);
                        this.$emit('update:member-details', data)
                    })
            },

            getPhoneCountryCodes() {
                return fetchJSON(`/api/members/GetPhoneCountryCodes`)
                    .then(data => {
                        this.countryCodes = data.countryCodes;
                        let airlineCountryTitle = (this.CONFIG.AirlineCountry || "").toUpperCase()
                        let airlineCountry = (this.countryCodes || []).find(code => code.title == airlineCountryTitle)
                        if (airlineCountry) {
                            
                            let { code: phoneCountryCode, country: airlineCountryCode } = airlineCountry
                            
                            // If the user doesn't have a country code set, default it to the airlineCountry
                            if (!this.phoneCountryCode) {
                                this.phoneCountryCode = phoneCountryCode
                            }

                            // Because user has no option to customize these fields, we wil set them for them
                            // based on the CountryCode of the airline
                            if (airlineCountryCode) {
                                this.nationalIdIssuingCountry = airlineCountryCode
                                this.passportIssuingCountry = airlineCountryCode
                                this.passportNationality = airlineCountryCode
                            }
                        }
                    })
                .catch(this.onThrow)
            },

            processMemberData(member) {
                if (!member) return;

                this.title = member.title;

                this.dateOfBirth = toDate(member.dateOfBirth);

                this.email = member.email;
                this.fullName = `${member.firstName} ${member.lastName}`;

                let phone = member.phone || {};
                this.phoneCountryCode = phone.countryCode;
                this.phoneNumber = phone.number;
                this.phoneType = phone.type;

                let phone2 = member.phone2 || [];
                this.phone2CountryCode = phone2.countryCode;
                this.phone2Number = phone2.number;
                this.phone2Type = phone2.type;

                let address = member.address || {};
                this.addressLineOne = address.address1;
                this.addressLineTwo = address.address2;
                this.addressCity = address.city;
                this.addressState = address.state;
                this.addressZip = address.zip;
                this.addressCountryCode = address.countryCode;

                let nationalId = member.nationalId || {};
                this.nationalIdNumber = nationalId.number;
                this.nationalIdIssuingCountry = nationalId.issuingCountry || this.CONFIG.AirlineCountry;

                let passport = member.passport || {};
                this.passportNumber = passport.number;
                this.passportExpires = toDate(passport.expires);
                this.passportIssuingCountry = passport.issuingCountry;
                this.passportNationality = passport.nationality;
            },

            updateMember() {
                this.checkForErrors();

                let errors = Object.values(this.errors)
                if (errors.length) {
                    alert(["Please fix the following errors:","", ...errors].join('\n'))
                }
                else if (this.submitting) {
                    alert('Update in process; please wait ...')
                }
                else {
                    this.$emit('submitting')
                    this.submitting = true

                    postJSON('/api/members/UpdateMember', {
                        member: this.memberUpdate,
                        allowDuplicateEmailAddress: this.allowDuplicateEmailAddress,
                    })
                        .then(() => {
                            this.$emit('close')
                        })
                        .catch(error => {
                            let conflicts = ((error || {}).error || {}).conflicts || []
                            if (conflicts.filter(c => c.code == 802).length) {
                                this.showDuplicateEmailCheckbox = this.portalInfo.allowEmailReuse
                                if (this.showDuplicateEmailCheckbox)
                                    return alert("Duplicate email address. Please confirm if you wish to reuse.")

                                //  Otherwise just show full error message
                            }

                            this.onThrow(error)
                        })
                        .finally(() => {
                            this.$emit('submitted')
                            this.submitting = false
                        })
                }
            },

            checkForErrors() {
                let validate = this.fieldsToValidate
                this.errors = {};

                if (validate.email) {
                    if (!this.email) {
                        this.errors["Email"] = "Email is required";
                    } else if (!/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(this.email)) {
                        this.errors["Email"] = "Invalid Email";
                    }
                    if (this.email && this.email.length > 50) {
                        this.errors["EmailTooLong"] = "Email address cannot exceed 50 characters in length";
                    }
                }

                if (validate.phone) {
                    //Phone Country Code Validation
                    const specialCharactersCheck = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
                    const lettersCheck = /[A-za-z]/;

                    if (this.phoneNumber) {
                        if (specialCharactersCheck.test(this.phoneNumber) && lettersCheck.test(this.phoneNumber)) {
                            this.errors["PhoneNumber"] = "Phone Number must not contain any letters or special characters";
                        } else if (specialCharactersCheck.test(this.phoneNumber)) {
                            this.errors["PhoneNumber"] = "Phone Number must not contain any special characters";
                        } else if (lettersCheck.test(this.phoneNumber)) {
                            this.errors["PhoneNumber"] = "Phone Number must not contain any letters";
                        }

                        if (this.phoneNumber.length > 20) {
                            this.errors["PhoneNumberTooLong"] = "Phone Number cannot exceed 20 characters in length";
                        }
                    }
                }

                if (validate.phone2) {
                    //Phone Country Code Validation
                    const specialCharactersCheck = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
                    const lettersCheck = /[A-za-z]/;

                    if (this.phone2Number) {
                        if (specialCharactersCheck.test(this.phone2Number) && lettersCheck.test(this.phone2Number)) {
                            this.errors["Phone2Number"] = "Phone Number must not contain any letters or special characters";
                        } else if (specialCharactersCheck.test(this.phone2Number)) {
                            this.errors["Phone2Number"] = "Phone Number must not contain any special characters";
                        } else if (lettersCheck.test(this.phone2Number)) {
                            this.errors["Phone2Number"] = "Phone Number must not contain any letters";
                        }

                        if (this.phone2Number.length > 20) {
                            this.errors["Phone2NumberTooLong"] = "Phone Number cannot exceed 20 characters in length";
                        }
                    }
                }

                if (validate.idDocument) {
                    //National ID
                    if (this.nationalIdNumber && this.nationalIdNumber.length > 20) {
                        this.errors["NatIdPassportTooLong"] = "National ID Number cannot exceed 20 characters in length"
                    }

                    //Passport
                    if (this.passportNumber && !this.passportIssuingCountry && !this.passportNationality) {
                        this.errors["PassportNationality"] = "Nationality is required when using a Passport.";
                        this.errors["PassportIssuingCountry"] = "Issuing Country is required when using a Passport";
                    } else if (this.passportNumber && !this.passportIssuingCountry && this.passportNationality) {
                        this.errors["PassportIssuingCountry"] = "Issuing Country is required when using a Passport";
                    } else if (this.passportNumber && this.passportIssuingCountry && !this.passportNationality) {
                        this.errors["PassportNationality"] = "Nationality is required when using a Passport";
                    }

                    if (this.passportNumber && this.passportNumber.length > 20) {
                        this.errors["PassportNumberTooLong"] = "Passport Number cannot exceed 20 characters in length";
                    }
                }

                if (validate.address) {
                    if (this.addressLineOne && this.addressLineOne.length > 75) {
                        this.errors["AddressLineOneTooLong"] = "Address Line One cannot exceed 75 characters in length";
                    }

                    if (this.addressLineTwo && this.addressLineTwo.length > 50) {
                        this.errors["AddressLineTwoTooLong"] = "Address Line Two cannot exceed 50 characters in length";
                    }

                    if (this.addressCity && this.addressCity.length > 40) {
                        this.errors["AddressCityTooLong"] = "City cannot exceed 40 characters in length";
                    }

                    if (this.addressState && this.addressState.length > 40) {
                        this.errors["AddressStateTooLong"] = "State/Province cannot exceed 40 characters in length";
                    }

                    if (this.addressZip && this.addressZip.length > 15) {
                        this.errors["AddressZipTooLong"] = "Postal Code cannot exceed 15 characters in length";
                    }
                }
            }
        },
        computed: {

            memberUpdate() {
                return {
                    title: this.title,
                    dateOfBirth: isoDate(this.dateOfBirth),
                    email: this.email,
                    address: {
                        address1: this.addressLineOne,
                        address2: this.addressLineTwo,
                        city: this.addressCity,
                        countryCode: this.addressCountryCode,
                        state: this.addressState,
                        zip: this.addressZip
                    },
                    phone: {
                        number: this.phoneNumber,
                        countryCode: this.phoneCountryCode,
                        type: this.phoneType
                    },
                    phone2: {
                        number: this.phone2Number,
                        countryCode: this.phone2CountryCode,
                        type: this.phone2Type
                    },
                    nationalId: {
                        number: this.nationalIdNumber,
                        issuingCountry: this.nationalIdIssuingCountry
                    },
                    passport: {
                        number: this.passportNumber,
                        expires: isoDate(this.passportExpires),
                        issuingCountry: this.passportIssuingCountry,
                        nationality: this.passportNationality
                    }
                }
            },

            fieldsToValidate() {
                let fieldsToValidate = {
                    email: true,
                    address: true,
                    phone: true,
                    phone2: true,
                    idDocument: true
                }
                return fieldsToValidate
            }
        }
    }
</script>